import { Component, inject, computed, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GuideService } from '../../../core/services/guide.service';
import { ButtonModule } from '@openng/optimus-ui/button';

@Component({
  selector: 'app-guide-overlay',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  styles: [`
    .guide-card {
      background: var(--stela-surface);
      border: 1px solid var(--stela-line);
      color: var(--stela-text-primary);
    }
    .guide-step-label {
      color: var(--stela-info);
    }
    .guide-close-btn {
      color: var(--stela-fg-4);
    }
    .guide-close-btn:hover {
      color: var(--stela-text-secondary);
    }
    .guide-content {
      color: var(--stela-text-primary);
    }
    .guide-footer {
      border-color: var(--stela-line-soft);
    }
    .guide-missing {
      color: var(--stela-fg-4);
    }
    .guide-arrow {
      background: var(--stela-surface);
      border-color: var(--stela-line);
    }
    .guide-highlight {
      border-color: var(--stela-info);
    }
  `],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (guideService.isActive() && guideService.currentStep()) {
      <div class="guide-card fixed z-9999 transition-all duration-300 ease-in-out shadow-xl rounded-lg p-4 w-[320px]"
           [style.top.px]="overlayPosition().top"
           [style.left.px]="overlayPosition().left">

        <div class="flex justify-between items-start mb-2">
            <span class="guide-step-label font-bold text-xs uppercase tracking-wide">
                Paso {{ guideStepIndex() + 1 }} de {{ guideStepsLength() }}
            </span>
            <button class="guide-close-btn transition-colors" (click)="guideService.stopGuide()">
                <i class="pi pi-times"></i>
            </button>
        </div>

        <div class="guide-content text-sm mb-4 leading-relaxed font-medium">
            {{ guideService.currentStep()?.content }}
        </div>

        <div class="guide-footer flex justify-between items-center pt-2 border-t">
             <div class="guide-missing text-xs italic" *ngIf="!guideService.targetRect()">
                 Elemento no encontrado
             </div>
             <div *ngIf="guideService.targetRect()"></div> <!-- Spacer -->

             <div class="flex gap-2">
                <p-button *ngIf="guideStepIndex() > 0"
                        label="Atrás"
                        icon="pi pi-arrow-left"
                        size="small"
                        [text]="true"
                        styleClass="text-xs px-2 py-1"
                        (onClick)="guideService.prevStep()"></p-button>

                <p-button *ngIf="guideStepIndex() < guideStepsLength() - 1"
                        label="Siguiente"
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        size="small"
                        styleClass="text-xs px-3 py-1"
                        (onClick)="guideService.nextStep()"></p-button>

                <p-button *ngIf="guideStepIndex() === guideStepsLength() - 1"
                        label="Finalizar"
                        icon="pi pi-check"
                        size="small"
                        severity="success"
                        styleClass="text-xs px-3 py-1"
                        (onClick)="guideService.stopGuide()"></p-button>
            </div>
        </div>

        <!-- Arrow pointing to target (Top) -->
        <div *ngIf="showArrowTop()"
             class="guide-arrow absolute w-4 h-4 border-l border-t transform rotate-45 -top-2 left-1/2 -ml-2">
        </div>

         <!-- Arrow pointing to target (Bottom) -->
         <div *ngIf="showArrowBottom()"
             class="guide-arrow absolute w-4 h-4 border-r border-b transform rotate-45 -bottom-2 left-1/2 -ml-2">
        </div>
      </div>

      <!-- Highlight box around target -->
      <div *ngIf="guideService.targetRect()"
           class="guide-highlight fixed z-9998 border-2 rounded-md pointer-events-none transition-all duration-300 ease-in-out box-content shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]"
           [style.top.px]="guideService.targetRect()!.top - 4"
           [style.left.px]="guideService.targetRect()!.left - 4"
           [style.width.px]="guideService.targetRect()!.width + 8"
           [style.height.px]="guideService.targetRect()!.height + 8">
      </div>
    }
  `
})
export class GuideOverlayComponent {
  guideService = inject(GuideService);

  // Computed helpers for template to reduce strict template checking noise
  guideStepIndex = computed(() => this.guideService.currentStepIndex());
  guideStepsLength = computed(() => this.guideService.steps().length);

  overlayPosition = computed(() => {
    const rect = this.guideService.targetRect();
    const cardWidth = 320;
    const cardHeight = 150; // Approximated
    
    if (!rect) {
      // Center on screen if no target
      return { 
        top: window.innerHeight / 2 - (cardHeight / 2), 
        left: window.innerWidth / 2 - (cardWidth / 2) 
      };
    }

    // Default: Place below the element
    let top = rect.bottom + 16;
    let left = rect.left + (rect.width / 2) - (cardWidth / 2);

    // If matches bottom edge, flip to top
    if (top + cardHeight > window.innerHeight) {
        top = rect.top - cardHeight - 16;
    }

    // Keep within horizontal bounds
    if (left < 16) left = 16;
    if (left + cardWidth > window.innerWidth - 16) {
        left = window.innerWidth - cardWidth - 16;
    }

    return { top, left };
  });

  showArrowTop = computed(() => {
      const rect = this.guideService.targetRect();
      if (!rect) return false;
      const pos = this.overlayPosition();
      return pos.top > rect.bottom; // Overlay is below target
  });

  showArrowBottom = computed(() => {
      const rect = this.guideService.targetRect();
      if (!rect) return false;
      const pos = this.overlayPosition();
      return pos.top < rect.top; // Overlay is above target
  });
}
